import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  MapPin, BedDouble, Star, ArrowRight, Building, Shield, Clock,
  Search, Users, CheckCircle, Wifi, PhoneCall, CreditCard,
  ChevronDown, ChevronUp, BookOpen, Train, Coffee, Sparkles,
} from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import { stationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const FAQ_DATA = [
  {
    q: 'What is a railway retiring room?',
    a: 'A railway retiring room is a short-stay accommodation facility located within or near railway station premises. These rooms are designed for travelers who need rest during transit, layovers, or early morning/late night arrivals. They offer comfortable beds, clean washrooms, and basic amenities at affordable rates.',
  },
  {
    q: 'How do I book a retiring room online?',
    a: 'Simply visit our website, select your preferred station from the homepage, browse available room categories (AC, Non-AC, Dormitory, Deluxe), choose your preferred time slot, and submit a booking request. You will receive a booking reference ID to track your reservation status.',
  },
  {
    q: 'What types of rooms are available?',
    a: 'We offer a variety of room types to suit every budget — AC Rooms, Non-AC Rooms, Deluxe Suites, and Dormitory beds. Each category comes with different amenities such as Wi-Fi, attached bathrooms, bedding, and more.',
  },
  {
    q: 'Can I book a room for just a few hours?',
    a: 'Yes! Our retiring rooms offer flexible time slots including 12-hour, 24-hour, and custom duration stays. This makes them perfect for short layovers, transit waits, or quick rest stops during long train journeys.',
  },
  {
    q: 'Is advance booking available?',
    a: 'Yes, we offer advance booking for registered users. Sign in with your Google account to access advance booking, manage your reservations, and enjoy a seamless experience across all stations in our network.',
  },
  {
    q: 'What amenities are provided in the rooms?',
    a: 'Standard amenities include clean bedding, towels, attached washrooms, drinking water, and 24/7 security. Depending on the room category, you may also get air conditioning, Wi-Fi, television, room service, and complimentary breakfast.',
  },
];

const HOME_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Retiring Room',
    url: 'https://retiringroom.in',
    description:
      'Book comfortable retiring rooms at major railway and transit stations across India. AC/Non-AC rooms, dormitories, and deluxe suites.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://retiringroom.in/station/{station_id}',
      'query-input': 'required name=station_id',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_DATA.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  },
];

const TESTIMONIALS = [
  {
    name: 'Rajesh Kumar',
    location: 'Mumbai',
    text: 'Extremely convenient during my overnight layover. The room was clean, well-maintained, and the booking process was effortless. Highly recommended for frequent travelers.',
    rating: 5,
  },
  {
    name: 'Priya Sharma',
    location: 'Delhi',
    text: 'I was amazed by the quality of service at such affordable prices. The AC room was spotless and the staff was very helpful. Will definitely use this service again.',
    rating: 5,
  },
  {
    name: 'Anand Patel',
    location: 'Ahmedabad',
    text: 'Perfect solution for my early morning train connection. The dormitory was comfortable, secure, and right inside the station. The online booking saved me so much hassle.',
    rating: 4,
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    icon: Search,
    title: 'Select Your Station',
    description: 'Browse through our network of retiring rooms across major railway stations in India and choose your preferred location.',
  },
  {
    step: 2,
    icon: BedDouble,
    title: 'Choose Your Room',
    description: 'Pick from AC, Non-AC, Deluxe, or Dormitory rooms. View pricing for different time slots and check real-time availability.',
  },
  {
    step: 3,
    icon: CreditCard,
    title: 'Book & Pay',
    description: 'Fill in your details, upload ID proof, and submit your booking request. Pay securely online or at the station counter.',
  },
  {
    step: 4,
    icon: CheckCircle,
    title: 'Check In & Relax',
    description: 'Track your booking status with your reference ID. Check in at the station and enjoy a comfortable stay during your journey.',
  },
];

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const fetchStations = async () => {
    try {
      const response = await stationsAPI.getAll();
      setStations(response.data.stations);
    } catch (error) {
      console.error('Error fetching stations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = (e) => {
    e.preventDefault();
    if (bookingId.trim()) {
      navigate(`/booking/status/${bookingId.trim()}`);
      setBookingId('');
      setShowStatusModal(false);
    }
  };

  return (
    <PublicLayout>
      <SEO
        canonical="/"
        keywords="retiring room, railway retiring room, station accommodation, transit station rooms, book retiring room online, railway station hotel, dormitory booking, AC retiring room, Bhusaval retiring room, Kannur retiring room"
        jsonLd={HOME_JSON_LD}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-midnight-950 via-midnight-900 to-railway-950 text-white overflow-hidden">
        <div className="absolute inset-0 bg-railway-pattern opacity-30 pointer-events-none" aria-hidden="true"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-midnight-950/80 to-transparent pointer-events-none" aria-hidden="true"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-railway-500/20 backdrop-blur-sm border border-railway-500/30 rounded-full px-4 py-2 mb-6 animate-fade-in">
                <Star className="w-4 h-4 text-railway-400" aria-hidden="true" />
                <span className="text-sm font-medium text-railway-300">Trusted by Travelers Nationwide</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight animate-slide-up">
                Rest & Refresh at
                <span className="text-railway-400 block">Transit Stations</span>
              </h1>

              <p className="mt-6 text-lg text-white/70 max-w-xl animate-slide-up" style={{ animationDelay: '0.1s' }}>
                Book comfortable retiring rooms at major railway stations across India.
                AC rooms, dormitories, and deluxe suites — all available with instant online booking.
              </p>

              <div className="mt-8 flex flex-wrap gap-4 animate-slide-up" style={{ animationDelay: '0.15s' }}>
                <button
                  onClick={() => document.getElementById('stations')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 bg-railway-500 hover:bg-railway-600 px-6 py-3 rounded-xl font-semibold transition-all shadow-lg shadow-railway-500/30"
                >
                  <BedDouble className="w-5 h-5" aria-hidden="true" />
                  Browse Stations
                </button>
                <button
                  onClick={() => setShowStatusModal(true)}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-6 py-3 rounded-xl font-semibold transition-all"
                >
                  <Search className="w-5 h-5" aria-hidden="true" />
                  Check Booking Status
                </button>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4 text-sm text-white/50 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" aria-hidden="true" />
                  <span>500+ Happy Travelers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" aria-hidden="true" />
                  <span>Secure Booking</span>
                </div>
              </div>
            </div>

            <div className="space-y-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {[
                { icon: Clock, title: 'Quick Booking', desc: 'Request a room in under 2 minutes with our streamlined booking process' },
                { icon: Shield, title: 'Secure & Safe', desc: 'Safe accommodation within station premises with 24/7 security' },
                { icon: BedDouble, title: 'Multiple Options', desc: 'AC/Non-AC, Deluxe Suites, Dormitory beds and more to fit every budget' },
              ].map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-5 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15 transition-colors"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="w-12 h-12 bg-railway-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-railway-500/30">
                    <feature.icon className="w-6 h-6 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg text-white">{feature.title}</h3>
                    <p className="text-white/70 mt-1">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute right-0 top-1/4 w-96 h-96 bg-railway-500/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>
        <div className="absolute left-1/4 bottom-0 w-64 h-64 bg-midnight-500/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-b from-midnight-50 to-white" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-railway-500 font-semibold text-sm uppercase tracking-wider mb-3">Simple Process</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-midnight-900">
              How to Book a Retiring Room
            </h2>
            <p className="mt-4 text-midnight-600 max-w-2xl mx-auto">
              Booking a comfortable retiring room at a railway station is quick and easy. Follow these four simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {HOW_IT_WORKS_STEPS.map((item) => (
              <div key={item.step} className="relative text-center group">
                <div className="w-16 h-16 bg-railway-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-railway-500/30 group-hover:scale-110 transition-transform">
                  <item.icon className="w-8 h-8 text-white" aria-hidden="true" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-midnight-900 rounded-full flex items-center justify-center text-white text-sm font-bold md:right-auto md:left-1/2 md:translate-x-4">
                  {item.step}
                </div>
                <h3 className="mt-6 text-lg font-display font-semibold text-midnight-900">{item.title}</h3>
                <p className="mt-3 text-midnight-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stations List */}
      <section className="py-20 bg-white" id="stations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-railway-500 font-semibold text-sm uppercase tracking-wider mb-3">Our Network</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-midnight-900">
              Select Your Station
            </h2>
            <p className="mt-3 text-midnight-600 max-w-2xl mx-auto">
              Choose from our network of retiring rooms across major railway stations in India. Each station offers clean, comfortable rooms with flexible time slots.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : stations.length === 0 ? (
            <div className="text-center py-20">
              <Building className="w-16 h-16 text-midnight-300 mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-xl font-semibold text-midnight-700">No stations available</h3>
              <p className="text-midnight-500 mt-2">Please check back later</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stations.map((station, index) => (
                <Link
                  key={station._id}
                  to={`/station/${station._id}`}
                  className="group card hover:shadow-2xl transition-all duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  aria-label={`View rooms at ${station.name} station in ${station.city}, ${station.state}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-railway-500 to-railway-600 rounded-xl flex items-center justify-center shadow-lg shadow-railway-500/30">
                        <span className="text-white font-bold text-lg" aria-hidden="true">{station.code}</span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-midnight-300 group-hover:text-railway-500 group-hover:translate-x-1 transition-all" aria-hidden="true" />
                    </div>

                    <h3 className="font-display font-semibold text-xl text-midnight-900 group-hover:text-railway-600 transition-colors">
                      {station.name}
                    </h3>

                    <div className="flex items-center gap-2 mt-2 text-midnight-500">
                      <MapPin className="w-4 h-4" aria-hidden="true" />
                      <span>{station.city}, {station.state}</span>
                    </div>

                    {station.contact && (
                      <p className="mt-3 text-sm text-midnight-400">
                        Contact: {station.contact}
                      </p>
                    )}
                  </div>

                  <div className="px-6 py-4 bg-midnight-50 border-t border-midnight-100 group-hover:bg-railway-50 transition-colors">
                    <span className="text-sm font-medium text-railway-600">
                      View Available Rooms →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Room Types / Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-midnight-50 to-white" id="room-types">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-railway-500 font-semibold text-sm uppercase tracking-wider mb-3">Our Offerings</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-midnight-900">
              Room Types for Every Traveler
            </h2>
            <p className="mt-4 text-midnight-600 max-w-2xl mx-auto">
              From budget-friendly dormitories to luxurious deluxe suites, we have the perfect room for your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'AC Room',
                description: 'Climate-controlled rooms with comfortable beds, attached bathroom, clean bedding, and modern amenities for a refreshing stay.',
                amenities: ['Air Conditioning', 'Attached Bathroom', 'Clean Bedding', 'Wi-Fi'],
                color: 'from-blue-500 to-blue-600',
                icon: Sparkles,
              },
              {
                title: 'Non-AC Room',
                description: 'Well-ventilated rooms with ceiling fans, comfortable beds, and all basic amenities at budget-friendly rates.',
                amenities: ['Ceiling Fan', 'Attached Bathroom', 'Clean Bedding', 'Drinking Water'],
                color: 'from-green-500 to-green-600',
                icon: BedDouble,
              },
              {
                title: 'Deluxe Suite',
                description: 'Premium rooms with enhanced amenities including television, mini-fridge, room service, and superior furnishing.',
                amenities: ['AC', 'Television', 'Room Service', 'Premium Bedding'],
                color: 'from-railway-500 to-railway-600',
                icon: Star,
              },
              {
                title: 'Dormitory',
                description: 'Shared accommodation with individual beds, lockers for valuables, and shared washroom facilities at the lowest rates.',
                amenities: ['Individual Bed', 'Locker', 'Shared Washroom', 'Affordable'],
                color: 'from-purple-500 to-purple-600',
                icon: Users,
              },
            ].map((room) => (
              <article key={room.title} className="card group hover:shadow-2xl transition-all duration-300">
                <div className={`p-6 bg-gradient-to-br ${room.color} text-white`}>
                  <room.icon className="w-10 h-10 mb-3" aria-hidden="true" />
                  <h3 className="text-xl font-display font-bold">{room.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-midnight-600 text-sm leading-relaxed">{room.description}</p>
                  <ul className="mt-4 space-y-2" role="list">
                    {room.amenities.map((amenity) => (
                      <li key={amenity} className="flex items-center gap-2 text-sm text-midnight-700">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" />
                        {amenity}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white" id="about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-railway-500 font-semibold text-sm uppercase tracking-wider mb-3">About Us</span>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-midnight-900 leading-tight">
                Your Trusted Partner for <span className="text-railway-500">Station Accommodation</span>
              </h2>
              <p className="mt-6 text-midnight-600 leading-relaxed">
                Retiring Room is India's premier platform for booking comfortable accommodation at railway and transit stations.
                Operated by <strong>Junes Kitchen and Bakers</strong>, we combine hospitality excellence with the convenience of station-side lodging to
                serve thousands of travelers every month.
              </p>
              <p className="mt-4 text-midnight-600 leading-relaxed">
                Whether you're on a long-distance train journey with a late-night arrival, need a place to freshen up during a layover,
                or require a comfortable room near the station for an early morning departure — our retiring rooms provide the perfect solution.
                Every room is maintained to the highest standards of cleanliness and comfort.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6">
                {[
                  { label: 'Stations Served', value: 'Multiple' },
                  { label: 'Happy Travelers', value: '500+' },
                  { label: 'Room Types', value: '4+' },
                  { label: 'Support', value: '24/7' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 rounded-xl bg-midnight-50">
                    <p className="text-2xl font-display font-bold text-railway-500">{stat.value}</p>
                    <p className="text-sm text-midnight-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-railway-100 to-railway-50 rounded-3xl p-8 lg:p-12">
                <div className="space-y-6">
                  {[
                    { icon: Train, title: 'Station-Side Location', text: 'All rooms are located within or adjacent to the railway station premises for maximum convenience.' },
                    { icon: Wifi, title: 'Modern Amenities', text: 'Wi-Fi, air conditioning, clean bedding, attached washrooms, and 24/7 hot water in select rooms.' },
                    { icon: Coffee, title: 'Food & Refreshments', text: 'Backed by Junes Kitchen and Bakers — enjoy fresh meals and snacks delivered to your room.' },
                    { icon: PhoneCall, title: 'Round-the-Clock Support', text: 'Our dedicated support team is available 24/7 to assist with bookings, check-ins, and any queries.' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                        <item.icon className="w-5 h-5 text-railway-500" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-midnight-900">{item.title}</h3>
                        <p className="text-sm text-midnight-600 mt-1">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20 bg-gradient-to-br from-midnight-950 via-midnight-900 to-railway-950 text-white" id="testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-railway-400 font-semibold text-sm uppercase tracking-wider mb-3">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold">
              What Our Travelers Say
            </h2>
            <p className="mt-4 text-white/60 max-w-2xl mx-auto">
              Hear from travelers who have experienced the comfort and convenience of our retiring rooms across India.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <article
                key={index}
                className="p-8 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15 transition-colors"
              >
                <div className="flex gap-1 mb-4" aria-label={`Rating: ${testimonial.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < testimonial.rating ? 'text-railway-400 fill-railway-400' : 'text-white/20'}`}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <blockquote className="text-white/80 leading-relaxed italic">
                  "{testimonial.text}"
                </blockquote>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-railway-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-white/50">{testimonial.location}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section> */}

      {/* Blog Preview / Travel Guides Section */}
      <section className="py-20 bg-white" id="travel-guides">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-railway-500 font-semibold text-sm uppercase tracking-wider mb-3">Travel Guides</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-midnight-900">
              Explore Destinations Near Our Stations
            </h2>
            <p className="mt-4 text-midnight-600 max-w-2xl mx-auto">
              Discover the rich culture, history, and attractions of cities connected through our retiring room network.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Link to="/blog/travel-guide-bhusaval" className="group card hover:shadow-2xl transition-all duration-300 overflow-hidden">
              <div className="relative h-56 bg-gradient-to-br from-railway-400 to-railway-600 flex items-center justify-center">
                <img
                  src="/Bhusaval_Jn_Entrance.jpg"
                  alt="Scenic railway view near Bhusaval Junction, Maharashtra — a major rail hub in Central India"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-block bg-railway-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">Maharashtra</span>
                  <h3 className="text-xl font-display font-bold text-white">Travel Guide: Bhusaval</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-midnight-600 text-sm leading-relaxed">
                  Discover Bhusaval — a major railway junction in Maharashtra known for its historical significance, delicious Khandeshi cuisine,
                  and proximity to the world-famous Ajanta and Ellora caves. A perfect stopover for travelers.
                </p>
                <span className="inline-flex items-center gap-1 mt-4 text-railway-500 font-semibold text-sm group-hover:gap-2 transition-all">
                  Read Full Guide <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </span>
              </div>
            </Link>

            <Link to="/blog/travel-guide-kannur" className="group card hover:shadow-2xl transition-all duration-300 overflow-hidden">
              <div className="relative h-56 bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                <img
                  src="https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=800&q=80"
                  alt="Beautiful coastal landscape of Kannur, Kerala — known for pristine beaches and rich heritage"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-block bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">Kerala</span>
                  <h3 className="text-xl font-display font-bold text-white">Travel Guide: Kannur</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-midnight-600 text-sm leading-relaxed">
                  Explore Kannur — Kerala's crown jewel of culture, home to Theyyam rituals, pristine Muzhappilangad Beach,
                  St. Angelo Fort, and rich spice heritage. An unmissable destination on the Malabar coast.
                </p>
                <span className="inline-flex items-center gap-1 mt-4 text-railway-500 font-semibold text-sm group-hover:gap-2 transition-all">
                  Read Full Guide <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-midnight-900 hover:bg-midnight-800 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
            >
              <BookOpen className="w-5 h-5" aria-hidden="true" />
              View All Travel Guides
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-midnight-50 to-white" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-railway-500 font-semibold text-sm uppercase tracking-wider mb-3">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-midnight-900">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-midnight-600 max-w-2xl mx-auto">
              Everything you need to know about booking and staying at our railway station retiring rooms.
            </p>
          </div>

          <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
            {FAQ_DATA.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-midnight-100 shadow-sm overflow-hidden"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-midnight-50 transition-colors"
                  aria-expanded={openFaqIndex === index}
                >
                  <h3 className="font-display font-semibold text-midnight-900 pr-4" itemProp="name">
                    {faq.q}
                  </h3>
                  {openFaqIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-railway-500 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-midnight-400 flex-shrink-0" aria-hidden="true" />
                  )}
                </button>
                <div
                  className="px-6 overflow-hidden transition-all duration-300"
                  style={{
                    maxHeight: openFaqIndex === index ? '500px' : '0',
                    paddingBottom: openFaqIndex === index ? '1.5rem' : '0',
                    opacity: openFaqIndex === index ? 1 : 0,
                  }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p className="text-midnight-600 leading-relaxed" itemProp="text">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-railway-500 to-railway-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold">
            Ready to Book Your Retiring Room?
          </h2>
          <p className="mt-4 text-white/80 text-lg max-w-2xl mx-auto">
            Whether you need a quick rest during a layover or a comfortable overnight stay at the station,
            we have the perfect room waiting for you. Book now and travel stress-free.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => document.getElementById('stations')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-white text-railway-600 px-8 py-4 rounded-xl font-bold transition-all hover:bg-railway-50 shadow-lg"
            >
              <BedDouble className="w-5 h-5" aria-hidden="true" />
              Book a Room Now
            </button>
            <Link
              to="/user/login"
              className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-xl font-bold transition-all"
            >
              <Users className="w-5 h-5" aria-hidden="true" />
              Sign Up for Advance Booking
            </Link>
          </div>
        </div>
      </section>

      {/* Check Booking Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Check Booking Status">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-slide-up">
            <div className="p-6 border-b border-midnight-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-display font-bold text-midnight-900">
                  Check Booking Status
                </h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="p-2 hover:bg-midnight-100 rounded-lg transition-colors"
                  aria-label="Close dialog"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-midnight-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-midnight-600 mt-2 text-sm">
                Enter your booking reference ID to check the current status of your reservation.
              </p>
            </div>

            <form onSubmit={handleCheckStatus} className="p-6 space-y-4">
              <div>
                <label htmlFor="booking-id-input" className="block text-sm font-medium text-midnight-700 mb-2">
                  Booking Reference ID
                </label>
                <input
                  id="booking-id-input"
                  type="text"
                  value={bookingId}
                  onChange={(e) => setBookingId(e.target.value)}
                  placeholder="e.g., 67890abcdef12345..."
                  className="w-full px-4 py-3 border border-midnight-200 rounded-xl focus:ring-2 focus:ring-railway-500 focus:border-railway-500 outline-none transition-all"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 px-4 py-3 border border-midnight-200 text-midnight-700 rounded-xl hover:bg-midnight-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!bookingId.trim()}
                  className="flex-1 px-4 py-3 bg-railway-500 text-white rounded-xl hover:bg-railway-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" aria-hidden="true" />
                  Check Status
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default Home;
